import React, { ChangeEvent, useMemo } from 'react';
import {
  ButtonPreviewNextQuestion,
  QuestionsSelect,
  QuestionsSelectArea,
  SelectReviewItems,
  WrongCorrectInformation,
} from './styles';

import arrowPrev from 'img/arrowPrev.svg';
import arrowNext from 'img/arrowNext.svg';

import {
  LectureQuestion,
  LectureAnswersState,
  isQuestionAnswered,
  ReviewQuestion,
  IDK_ANSWER_ID,
} from '../../types';
import { PanelHeader } from '../commons/styles';
import { QUESTIONS_SELECT_AREA_ID } from '../../utils';
import { getEntryKey, buildStepKey } from '../../utils/questionIdentity';

export interface LecturesHeaderPanelProps {
  currentLecture: LectureQuestion[];
  questionSelected?: LectureQuestion | null;
  answers: LectureAnswersState;
  /** Recebe a chave da ENTRADA (`getEntryKey`), não o `id_questao` */
  handleSelectQuestion: (entryKey: string) => void;
  handleViewNextsQuestions: () => void;
  handleViewPreviewQuestions: () => void;
  widthComponentQuestionsSelectArea: {
    scrollWidth: number;
    clientWidth: number;
    scrollLeft: number;
  };
  isReview?: boolean;
  testReviewTime?: string;
  countCorrectQuestions?: () => { correctQuestions: number; totalQuestions: number };
  reviewType?: {
    title: string;
    value: string;
  };
  handleChangeItemReview?: (e: ChangeEvent<HTMLSelectElement>) => void;
  itemsSelectOnReview?: {
    title: string;
    value: string;
  }[];
  questionsReview?: ReviewQuestion[];
  /** `true` depois de o payload da revisão chegar — inclusive quando ele vem vazio
   * (prova entregue sem nenhuma resposta). Sem esta prop, o componente cai no
   * heurístico de tamanho, que não distingue os dois casos. */
  reviewLoaded?: boolean;
  isSimulated?: boolean;
  questionOrderMap?: Record<string, number>;
  /** Numeração original (expandida) de cada questão/step na prova completa, chaveada por
   * `getEntryKey` (questões tradicionais) ou `buildStepKey` (steps do Estudo de Caso).
   * Preserva a numeração original quando um filtro de revisão está ativo. */
  expandedOriginalNumbers?: Record<string, number>;
  /** Índice do step atual para questões Estudo de Caso */
  caseStudyStepIndex?: number;
  /** Navega para um step específico: recebe a chave da ENTRADA e o índice do step nela */
  onSelectCaseStudyStep?: (entryKey: string, stepIndex: number) => void;
}

export function LecturesHeaderPanel({
  currentLecture,
  questionSelected,
  answers,
  handleSelectQuestion,
  handleViewNextsQuestions,
  handleViewPreviewQuestions,
  widthComponentQuestionsSelectArea,
  isReview = false,
  testReviewTime,
  countCorrectQuestions,
  reviewType,
  handleChangeItemReview,
  itemsSelectOnReview,
  questionsReview,
  reviewLoaded,
  isSimulated,
  questionOrderMap,
  expandedOriginalNumbers,
  caseStudyStepIndex = 0,
  onSelectCaseStudyStep,
}: LecturesHeaderPanelProps) {
  // Expande a lista de questões: case_study vira N chips (um por step).
  // O número exibido usa a numeração original da prova completa (expandedOriginalNumbers),
  // para não renumerar as questões quando um filtro de revisão está ativo.
  const flatItems = useMemo(() => {
    let counter = 0;
    const items: Array<{
      key: string;
      entryKey: string;
      questionId: number;
      stepIndex: number | null;
      stepId?: number;
      displayNumber: number;
      question: LectureQuestion;
    }> = [];

    currentLecture.forEach((question) => {
      const entryKey = getEntryKey(question);

      if (question.question_type === 'case_study') {
        (question.step_multiple ?? []).forEach((step, stepIdx) => {
          counter++;
          const key = buildStepKey(question, step.id);
          items.push({
            key,
            entryKey,
            questionId: question.id_questao,
            stepIndex: stepIdx,
            stepId: step.id,
            displayNumber: expandedOriginalNumbers?.[key] ?? counter,
            question,
          });
        });
      } else {
        counter++;
        items.push({
          key: entryKey,
          entryKey,
          questionId: question.id_questao,
          stepIndex: null,
          displayNumber: expandedOriginalNumbers?.[entryKey] ?? counter,
          question,
        });
      }
    });
    return items;
  }, [currentLecture, expandedOriginalNumbers]);

  // Na revisão o payload é a única fonte de verdade da cor dos chips: questão (ou step)
  // sem entrada nunca foi respondida — caso típico de questão incluída na aula depois de
  // o aluno entregar a prova — e recebe a mesma cor de "Não sei".
  //
  // `reviewLoaded` é o que segura o flash: a página chama `setReviewLecture`
  // ({ isReview: true }) antes de o payload chegar em `setQuestionsReview`, e inicia
  // `questionsReview` como `[]` — colorir antes do payload pintaria todos os chips de
  // roxo durante o carregamento. Usar o flag explícito (em vez do tamanho do payload)
  // é o que permite colorir também a prova entregue 100% em branco, cujo payload chega
  // legitimamente vazio. Se a prop não for informada, cai no heurístico de tamanho.
  // Fora da revisão o payload é ignorado por completo.
  const reviewList = questionsReview ?? [];
  const hasReviewData = isReview && (reviewLoaded ?? reviewList.length > 0);
  const selectedEntryKey = questionSelected ? getEntryKey(questionSelected) : null;

  return useMemo(
    () => (
      <PanelHeader data-testid="lecturesheaderpanel-panelheader">
        {isReview && countCorrectQuestions && (
          <WrongCorrectInformation data-testid="lecturesheaderpanel-incorrectcorrect-information">
            Você acertou{' '}
            <span data-testid="lecturesheaderpanel-correct-questions">
              {countCorrectQuestions().correctQuestions} de {countCorrectQuestions().totalQuestions}{' '}
              questões
            </span>{' '}
            {isReview && isSimulated && (
              <span data-testid="lecturesheaderpanel-review-time">em {testReviewTime}</span>
            )}
            !
          </WrongCorrectInformation>
        )}
        {widthComponentQuestionsSelectArea.scrollLeft > 0 && (
          <ButtonPreviewNextQuestion
            onClick={handleViewPreviewQuestions}
            className={`preview ${isReview ? 'review' : 'lecture'}`}
            data-testid="lecturesheaderpanel-preview-button"
          >
            <img src={arrowPrev} alt="Anterior" data-testid="lecturesheaderpanel-arrowprev" />
          </ButtonPreviewNextQuestion>
        )}
        <QuestionsSelectArea
          id={QUESTIONS_SELECT_AREA_ID}
          data-testid="lecturesheaderpanel-questionsselect-area"
        >
          {flatItems.map((item) => {
            const { question, stepIndex, stepId, displayNumber, questionId } = item;
            const isCaseStudyStep = stepIndex !== null;
            let questionClass = '';

            if (isCaseStudyStep) {
              // Preenchimento da prova: qualquer resposta (inclusive "Não sei") → verde
              const stepAnswer =
                stepId !== undefined ? answers.caseStudy?.[questionId]?.[stepId] : undefined;
              if (stepAnswer !== undefined) {
                questionClass = 'correct';
              }

              // Revisão: IDK → roxo (.idk), certo → verde, errado → vermelho.
              // Step sem entrada no payload (step criado depois da entrega) → "Não sei".
              if (hasReviewData) {
                const stepReview = reviewList.find(
                  (q) =>
                    q.question_id === questionId &&
                    q.question_step_id === stepId,
                );
                if (stepReview && stepReview.question_type === 'case_study') {
                  const isIdkAnswer = stepReview.answer_id === IDK_ANSWER_ID;
                  questionClass = isIdkAnswer
                    ? 'idk'
                    : stepReview.is_correct
                      ? 'correct'
                      : 'incorrect';
                } else {
                  questionClass = 'idk';
                }
              }
            } else {
              const answered = isQuestionAnswered(question, answers);
              if (answered) questionClass = 'correct';

              // Questão sem entrada no payload (incluída na aula depois da entrega da
              // prova) nunca foi respondida → mesma cor de "Não sei".
              if (hasReviewData) {
                const questionReview = reviewList.find(
                  (q) => q.question_id === questionId,
                );
                if (questionReview) {
                  const isIdkAnswer =
                    questionReview.question_type === 'objective_questions' &&
                    questionReview.answer_id === IDK_ANSWER_ID;
                  questionClass = isIdkAnswer
                    ? 'idk'
                    : questionReview.is_correct
                      ? 'correct'
                      : 'incorrect';
                } else {
                  questionClass = 'idk';
                }
              }
            }

            const isChecked = isCaseStudyStep
              ? item.entryKey === selectedEntryKey && caseStudyStepIndex === stepIndex
              : item.entryKey === selectedEntryKey;

            const handleChange = () => {
              if (isCaseStudyStep && onSelectCaseStudyStep) {
                onSelectCaseStudyStep(item.entryKey, stepIndex!);
              } else {
                handleSelectQuestion(item.entryKey);
              }
            };

            return (
              <QuestionsSelect
                key={item.key}
                htmlFor={item.key}
                data-testid={`lecturesheaderpanel-question-select-${item.key}`}
              >
                <input
                  checked={isChecked}
                  type="radio"
                  id={item.key}
                  name="question"
                  value={item.key}
                  onChange={handleChange}
                  hidden
                  data-testid="lecturesheaderpanel-question-select-radio"
                />
                <span className={questionClass} data-testid="lecturesheaderpanel-question-class">
                  {displayNumber}
                </span>
              </QuestionsSelect>
            );
          })}
        </QuestionsSelectArea>
        {widthComponentQuestionsSelectArea.clientWidth +
          widthComponentQuestionsSelectArea.scrollLeft <
          widthComponentQuestionsSelectArea.scrollWidth && (
          <ButtonPreviewNextQuestion
            onClick={handleViewNextsQuestions}
            className={`next ${isReview ? 'review' : 'lecture'}`}
            data-testid="lecturesheaderpanel-next-button"
          >
            <img src={arrowNext} alt="Próximo" data-testid="lecturesheaderpanel-arrownext" />
          </ButtonPreviewNextQuestion>
        )}
        {isReview && itemsSelectOnReview && reviewType && handleChangeItemReview && (
          <SelectReviewItems
            onChange={handleChangeItemReview}
            value={reviewType.title}
            name="reviewType"
            data-testid="lecturesheaderpanel-select-review-items"
          >
            {itemsSelectOnReview.map((item: { title: string; value: string }) => (
              <option
                key={item.title}
                value={item.title}
                data-testid={`lecturesheaderpanel-select-review-item-${item.title}`}
              >
                {item.title}
              </option>
            ))}
          </SelectReviewItems>
        )}
      </PanelHeader>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      flatItems,
      currentLecture,
      questionSelected,
      answers,
      widthComponentQuestionsSelectArea,
      isReview,
      hasReviewData,
      questionsReview,
      reviewLoaded,
      testReviewTime,
      reviewType,
      questionOrderMap,
      caseStudyStepIndex,
      onSelectCaseStudyStep,
    ],
  );
}
